import React from 'react';
import { View as RNView, ViewProps } from 'react-native';
import Box from './Box';

// Simple View component that can be used as a drop-in replacement for native-base View
// It extends React Native's View with additional props for easier migration
interface ViewPropsExtended extends ViewProps {
  // Layout props
  flex?: number;
  flexDirection?: 'row' | 'column' | 'row-reverse' | 'column-reverse';
  justifyContent?: 'flex-start' | 'flex-end' | 'center' | 'space-between' | 'space-around' | 'space-evenly';
  alignItems?: 'flex-start' | 'flex-end' | 'center' | 'stretch' | 'baseline';
  alignSelf?: 'auto' | 'flex-start' | 'flex-end' | 'center' | 'stretch' | 'baseline';

  // Spacing props
  marginTop?: number | string;
  marginBottom?: number | string;
  marginLeft?: number | string;
  marginRight?: number | string;
  paddingTop?: number | string;
  paddingBottom?: number | string;
  paddingLeft?: number | string;
  paddingRight?: number | string;
  paddingX?: number | string; // paddingHorizontal
  paddingY?: number | string; // paddingVertical
  marginX?: number | string; // marginHorizontal
  marginY?: number | string; // marginVertical

  // Background
  bg?: string; // backgroundColor
  backgroundColor?: string;

  // Border
  rounded?: 'sm' | 'md' | 'lg' | 'xl' | 'full' | number;
  borderRadius?: number;
  borderWidth?: number | string;
  borderColor?: string;

  // Dimensions
  width?: number | string;
  height?: number | string;
  minWidth?: number | string;
  minHeight?: number | string;
  maxWidth?: number | string;
  maxHeight?: number | string;

  // NativeBase theme variants (we'll ignore these for now)
  _dark?: any;
  _light?: any;
  _hover?: any;
  _focus?: any;
  _pressed?: any;
  _disabled?: any;
}

const View: React.FC<ViewPropsExtended> = props => {
  const {
    flex,
    flexDirection,
    justifyContent,
    alignItems,
    alignSelf,
    marginTop,
    marginBottom,
    marginLeft,
    marginRight,
    paddingTop,
    paddingBottom,
    paddingLeft,
    paddingRight,
    paddingX,
    paddingY,
    marginX,
    marginY,
    bg,
    rounded,
    borderRadius,
    borderWidth,
    borderColor,
    width,
    height,
    minWidth,
    minHeight,
    maxWidth,
    maxHeight,
    _dark,
    _light,
    _hover,
    _focus,
    _pressed,
    _disabled,
  } = props;

  // If any enhanced props are used, use Box component
  const hasEnhancedProps =
    flex !== undefined ||
    flexDirection !== undefined ||
    justifyContent !== undefined ||
    alignItems !== undefined ||
    alignSelf !== undefined ||
    marginTop !== undefined ||
    marginBottom !== undefined ||
    marginLeft !== undefined ||
    marginRight !== undefined ||
    paddingTop !== undefined ||
    paddingBottom !== undefined ||
    paddingLeft !== undefined ||
    paddingRight !== undefined ||
    paddingX !== undefined ||
    paddingY !== undefined ||
    marginX !== undefined ||
    marginY !== undefined ||
    bg !== undefined ||
    rounded !== undefined ||
    borderRadius !== undefined ||
    borderWidth !== undefined ||
    borderColor !== undefined ||
    width !== undefined ||
    height !== undefined ||
    minWidth !== undefined ||
    minHeight !== undefined ||
    maxWidth !== undefined ||
    maxHeight !== undefined ||
    _dark !== undefined ||
    _light !== undefined ||
    _hover !== undefined ||
    _focus !== undefined ||
    _pressed !== undefined ||
    _disabled !== undefined;

  if (hasEnhancedProps) {
    return <Box {...props} />;
  }

  // For simple cases, just use React Native's View
  return <RNView {...props} />;
};

export default View;
